import {
    Box,
    Divider,
    Flex,
    Heading,
    HStack,
    Text,
    Tooltip,
} from '@chakra-ui/react'
import { Formik, useFormikContext } from 'formik'
import { InputControl } from 'formik-chakra-ui'
import * as Yup from 'yup'

import { defaultBorder, thickBoxShadow } from '../../../chakraTheme'
import AX_INSTRUCTIONS_ACTION from '../../../enums/axInstructionsActions'
import { ArrowForward, Info, PreviousArrow } from '../../Icons/default'
import PrimaryButton from '../../PrimaryButton.jsx'

const ImportantStar = () => (
    <Text as={'span'} color={'flushmahogany'}>
        *
    </Text>
)

const Form = () => {
    const labelProps = {
        fontSize: '0.875rem',
        fontWeight: 'normal',
        requiredIndicator: <ImportantStar />,
    }

    const baseInputProps = {
        border: '3px solid #333333 !important',
        borderRadius: 'none',
        _focus: {
            boxShadow: 'none',
        },
        fontSize: '1rem',
        padding: '1rem',
        height: '56px',
        width: '350px',
    }
    return (
        <>
            <Box mt={8} mb={'54px'}>
                <InputControl
                    labelProps={labelProps}
                    name="name"
                    label="Registered Name"
                    isRequired
                    inputProps={{
                        ...baseInputProps,
                        placeholder: 'Enter your registered name',
                    }}
                />
                <InputControl
                    labelProps={labelProps}
                    name="email"
                    label="Registered Email"
                    isRequired
                    mt={6}
                    inputProps={{
                        ...baseInputProps,
                        placeholder: 'Enter your registered email',
                    }}
                />
                <InputControl
                    labelProps={labelProps}
                    name="mobileNumber"
                    label="Registered Mobile"
                    isRequired
                    mt={6}
                    inputProps={{
                        ...baseInputProps,
                        placeholder: 'Enter your registered mobile',
                    }}
                />
                <InputControl
                    labelProps={labelProps}
                    name="schoolName"
                    label="University/College Name"
                    isRequired
                    mt={6}
                    inputProps={{
                        ...baseInputProps,
                        placeholder: 'Enter your university/college name',
                    }}
                />
            </Box>
        </>
    )
}

const NavFooter = ({ isFirstPage, dispatch }) => {
    const { isValid, values } = useFormikContext()
    const isNextDisabled = !isValid

    const handlePrevious = () => {
        dispatch({ type: AX_INSTRUCTIONS_ACTION.PREV_STEP })
    }
    const handleNext = () => {
        // save the form values to store
        dispatch({ type: AX_INSTRUCTIONS_ACTION.UPDATE_FORM, values })

        // move to next step
        dispatch({ type: AX_INSTRUCTIONS_ACTION.NEXT_STEP })
    }

    return (
        <Flex
            w={'full'}
            my={'6'}
            flexDirection={'row-reverse'}
            justifyContent={'space-between'}
        >
            <Tooltip
                isDisabled={!isNextDisabled}
                hasArrow
                label={
                    <Box fontWeight={'normal'}>
                        <HStack>
                            <Box mr={5}>
                                <Info h={6} w={6} />
                            </Box>
                            <Box marginInlineStart={'0 !important'}>
                                Please fill in all the required fields to
                                proceed.
                            </Box>
                        </HStack>
                    </Box>
                }
                placement="top-end"
                bg={'ziggurat'}
                color={'codgray'}
                padding={'6'}
                boxShadow={thickBoxShadow}
                border={defaultBorder}
                borderRadius={'none'}
                offset={[-4, 10]}
                sx={{
                    '.chakra-tooltip__arrow': {
                        boxShadow: '8px 2px 0 #333333 !important',
                    },
                }}
            >
                <Flex>
                    <PrimaryButton
                        wrapperProps={{
                            marginLeft: 'auto',
                            _hover: {},
                            onClick: e => {
                                if (isNextDisabled) return e.preventDefault()
                                handleNext()
                            },
                        }}
                        variant={isNextDisabled ? 'disabled' : 'clickable'}
                        rightIcon={
                            <ArrowForward
                                fontSize={'2xl'}
                                color={isNextDisabled ? 'alto2' : 'white'}
                            />
                        }
                        my={{ base: 4, md: 0 }}
                        w={{ base: 'full', md: 'auto' }}
                    >
                        Next
                    </PrimaryButton>
                </Flex>
            </Tooltip>
            <Flex
                align="center"
                cursor="pointer"
                onClick={handlePrevious}
                color="emperor"
                display={isFirstPage ? 'none' : 'flex'}
            >
                <PreviousArrow h={6} w={6} />
                <Heading
                    ml={2}
                    fontSize={{ base: 'sm', md: 'md' }}
                    fontWeight="bold"
                >
                    Previous
                </Heading>
            </Flex>
        </Flex>
    )
}

const DetailsForm = ({ store, dispatch, isFirstPage }) => {
    const initialValues = {
        name: '',
        email: '',
        mobileNumber: '',
        schoolName: '',
    }

    const validationSchema = Yup.object({
        name: Yup.string().trim().required('Name is a required field'),
        email: Yup.string()
            .trim()
            .email('Invalid email format')
            .required('Email is a required field'),
        mobileNumber: Yup.string()
            .matches(/^\d{10}$/, {
                message: 'Enter a valid 10 digit mobile number',
            })
            .required('Mobile number is a required field'),
        schoolName: Yup.string()
            .trim()
            .required('School name is a required field'),
    })

    return (
        <Box pl={10} pr={16} mt={48}>
            <Heading fontWeight={'extrabold'} fontSize={'2xl'}>
                Fill In Additional Details
            </Heading>
            <Text color={'#5c5c5c'} mt={2}>
                This assessment requires you to fill in additional details
                before proceeding.
            </Text>
            <Formik
                initialValues={store.formValues || initialValues}
                validationSchema={validationSchema}
                validateOnMount={true}
            >
                <Box>
                    <Form />
                    <Divider
                        borderBottom={'3px solid rgba(69, 69, 69, 0.2);'}
                    />
                    <NavFooter isFirstPage={isFirstPage} dispatch={dispatch} />
                </Box>
            </Formik>
        </Box>
    )
}

export default DetailsForm
